import React, { useEffect, useState } from 'react';
import { Archive, RefreshCw, Trash2, Loader2, ShieldCheck, X, BellRing } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const SUPER_ADMIN_EMAIL = 'contact.destinestudio@gmail.com';

/**
 * ModerationArchivesPanel — a notification-style archive of all moderation
 * actions logged via /api/admin/moderation-log/log. Visible only to
 * super admins and managers.
 */
const ModerationArchivesPanel = () => {
  const { user } = useAuth();
  const role = String(user?.admin_role || '').toLowerCase();
  const isManagerOrSuper = user?.is_super_admin || role === 'super_admin' || role === 'manager' || (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/moderation-log/log', { params: { limit: 200 } });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement archives');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const del = async (id) => {
    if (!window.confirm('Supprimer cette entrée d\'archive ?')) return;
    try {
      await axios.delete(`/api/admin/moderation-log/log/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success('Entrée supprimée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (!isManagerOrSuper) return null;

  const iconFor = (type) => {
    if (type === 'user') return '👤';
    if (type === 'group') return '👥';
    if (type === 'post' || type === 'gim_toli') return '🎙️';
    if (type === 'podcast') return '🎧';
    if (type === 'article') return '📰';
    if (type === 'ad') return '📢';
    return '📦';
  };

  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-sm font-medium hover:bg-amber-500/20"
        data-testid="open-moderation-archives"
      >
        <Archive className="w-4 h-4" />
        Archives modération (Manager+)
        <ShieldCheck className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto pt-8 pb-4 px-4" data-testid="moderation-archives-modal">
          <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-3xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-amber-400" />
                <h2 className="text-xl font-bold">Archives modération</h2>
                <span className="text-xs text-gray-400">{items.length} notification(s)</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={load} className="p-2 hover:bg-white/10 rounded-full" title="Rafraîchir">
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button onClick={() => setOpen(false)} className="p-2 hover:bg-white/10 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-gray-400 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
            ) : items.length === 0 ? (
              <p className="text-sm text-gray-400 py-6">Aucune action de modération enregistrée pour l&apos;instant.</p>
            ) : (
              <div className="space-y-2 max-h-[65vh] overflow-y-auto">
                {items.map((it) => (
                  <div key={it.id} className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-start gap-3" data-testid={`archive-log-${it.id}`}>
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center text-xl flex-shrink-0" aria-hidden>
                      <BellRing className="w-4 h-4 text-amber-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        <span aria-hidden className="mr-1">{iconFor(it.target_type)}</span>
                        <strong className="text-white">{it.admin_name}</strong>
                        <span className="text-gray-400"> a </span>
                        <span className="text-red-300">{it.action === 'deleted' ? 'supprimé' : it.action}</span>
                        <span className="text-gray-400"> le/la </span>
                        <span className="uppercase text-cyan-300">{it.target_type}</span>
                        {it.target_title && (
                          <> <span className="text-gray-400">« </span><strong className="text-white">{it.target_title}</strong><span className="text-gray-400"> »</span></>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1">
                        {(it.at || '').slice(0, 19).replace('T', ' ')} · id {it.target_id}
                        {it.reason && <span> · {it.reason}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => del(it.id)}
                      className="p-2 rounded-full hover:bg-red-500/10 text-red-400 flex-shrink-0"
                      title="Supprimer cette entrée"
                      data-testid={`delete-log-${it.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModerationArchivesPanel;
